import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// MUI Components
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Drawer from "@material-ui/core/Drawer";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import List from "@material-ui/core/List";
import Collapse from "@material-ui/core/Collapse";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";

// Icons
import MenuIcon from "@material-ui/icons/Menu";
import SaveIcon from "@material-ui/icons/Save";
import LockIcon from "@material-ui/icons/Lock";
import InfoIcon from "@material-ui/icons/Info";
import ListAltIcon from "@material-ui/icons/ListAlt";
import QuitIcon from "@material-ui/icons/ExitToApp";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import CloudIcon from "@material-ui/icons/Cloud";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import KeyboardIcon from "@material-ui/icons/Keyboard";
import UsbIcon from "@material-ui/icons/Usb";
import DeviceIcon from "@material-ui/icons/SettingsRemote";
import WifiIcon from "@material-ui/icons/Wifi";
import UpdateIcon from "@material-ui/icons/Update";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";

// Other
import SaveStudyDialog from "./SaveStudyDialog";
import LockStudyDialog from "./LockStudyDialog";
import SaveProtocolDialog from "./SaveProtocolDialog";
import ExportPdfDialog from "./ExportPdfDialog";
import ExportCsvDialog from "./ExportCsvDialog";
import WifiDialog from "./WifiDialog";
import ExportStudiesDialog from "./ExportStudiesDialog";
import SyncStudiesDialog from "./SyncStudiesDialog";
import QuitDialog from "./QuitDialog";

import { colors } from "../../constants/colors";
import { PdfIcon, CsvIcon } from "../../constants/customIcons";
import { customTheme } from "../../constants/customTheme";
import { mainAppSectionIds } from "../../constants/componentMap";
import { wifiEnabled } from "../../constants/constants";

//Actions
import {
  errorResponse,
  fetchSaveStudy,
  toggleLock,
  toggleLockDialog,
  fetchCheckSavedStudy,
  fetchCheckWifi,
  toggleSaveDialog,
  toggleProtocolDialog,
  setMainViewSection,
  toggleDiagnosticsDialog,
  toggleExportPdfDialog,
  toggleExportCsvDialog,
  toggleExportStudiesDialog,
  toggleVirtualKeyboardAllowed,
  setTheme,
  toggleSyncStudiesDialog,
  fetchRemoteStudiesList,
  toggleQuitDialog,
  setCreateProtocolMode,
  discardStudy,
  fetchGetAvailableDrives,
  fetchLocalStudiesList,
  fetchCloseDevice,
  toggleWifiDialog,
  fetchConnectDevice,
  toggleSkipDiagnosticsSetup,
  fetchCheckUpdate,
  setRememberMe,
} from "../../actions";
import { viewIds } from "../../constants/componentMap";

const useStyles = makeStyles({
  drawerRoot: (props) => ({
    "& .MuiDrawer-paper": {
      overflowX: "hidden",
      overflowY: "auto",
      width: "300px",
      ...customTheme(props.theme).customScrollBar,
      justifyContent: "space-between",
    },
    "& .MuiList-padding": {
      padding: "0px",
    },
  }),
  menuBtn: {
    padding: "6px",
    marginLeft: "10px",
  },
  listItem: {
    paddingTop: "0px",
    paddingBottom: "0px",
    minHeight: "40px",
  },
  listSubheader: {
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
    fontWeight: 400,
  },
  accountMenuItem: {
    backgroundColor: (props) => colors[props.theme].headerBkg,
    justifyContent: "center",
    alignItems: "center",
    fontSize: "1rem",
    fontWeight: 700,
    "& .MuiListItemIcon-root": {
      minWidth: "35px",
    },
  },
  divider: {
    margin: "10px 15px 0px 15px",
  },
  advancedSettingsCollapse: {
    paddingBottom: "40px",
  },
  versionListItem: {
    padding: "0px 16px 0px 71px",
    "& .MuiListItemText-root": {
      "& .MuiTypography-body1": {
        fontSize: "0.875rem",
      },
    },
  },
  noIconItem: {
    paddingLeft: "71px",
  },
});

const HeaderMenu = (props) => {
  const classes = useStyles(props);

  const [saveStudyPrompt, setSaveStudyPrompt] = React.useState({
    prompt: null,
  });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openShowAllSaveOptions, setOpenShowAllSaveOptions] =
    React.useState(false);
  const [openAdvanceSettings, setOpenAdvanceSettings] = React.useState(false);

  const pathArray = props.studySavePath.split("/");
  const initialPath = pathArray.splice(0, pathArray.length - 1).join("/");
  const fileName = pathArray.splice(-1)[0].split(".");
  const initialName = props.studyId;
  const extension = "." + fileName.splice(1, fileName.length).join(".");

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleCloseMenu() {
    setAnchorEl(null);
    setOpenShowAllSaveOptions(false);
    setOpenAdvanceSettings(false);
  }

  const handleHealthClick = () => {
    handleCloseMenu();
    setOpenShowAllSaveOptions(false);
    setOpenAdvanceSettings(false);
    props.toggleDiagnosticsDialog();
    props.toggleSkipDiagnosticsSetup();
  };

  const handleProtocolClick = () => {
    handleCloseMenu();
    setOpenShowAllSaveOptions(false);
    setOpenAdvanceSettings(false);
    props.toggleProtocolDialog();
  };

  const handleSaveClick = (prompt) => {
    setSaveStudyPrompt({ prompt: prompt });
    handleCloseMenu();
    setOpenShowAllSaveOptions(false);
    setOpenAdvanceSettings(false);
    if (
      (props.acquiring || !props.samplesDone || !props.samplesSaved) &&
      (prompt === "QUIT" || prompt === "LOGOUT")
    ) {
      props.toggleQuitDialog();
    } else if (props.acquiring || !props.samplesDone || !props.samplesSaved) {
      //if there is a sample processing or acquiring
      props.errorResponse("Cannot save study while processing/saving samples.");
    } else if (props.studySaved && (prompt === "QUIT" || prompt === "LOGOUT")) {
      props.discardStudy("QUIT");
      // props.fetchCheckSavedStudy(props.studySavePath, prompt); // check if any changes are made to the study since the last saved version
      // } else if (props.studySaved && prompt === "CONTINUE") {
      //   props.fetchSaveStudy(prompt, props.encryption);
    } else {
      props.toggleSaveDialog();
    }
  };

  const handleForceQuit = () => {
    props.discardStudy("QUIT");
    // if (props.studySaved) {
    //   props.fetchCheckSavedStudy(props.studySavePath, prompt); // check if any changes are made to the study since the last saved version
    // } else {
    //   props.toggleSaveDialog();
    // }
  };

  const handleToggleSimulate = (e) => {
    if (e.target.checked) {
      if (props.connectionEstablished) {
        props.fetchCloseDevice();
      }
      props.fetchConnectDevice(true);
      // props.toggleDiagnosticsDialog();
    } else {
      props.fetchCloseDevice();
    }

    handleCloseMenu();
  };

  const handleChangeRememberMe = () => {
    if (props.rememberMe) {
      // localStorage.setItem("username", null);
      // localStorage.setItem("password", null);
      props.setRememberMe(false);
    } else {
      // localStorage.setItem("username", props.username);
      // localStorage.setItem("password", props.password);
      props.setRememberMe(true);
    }
  };

  // MAIN APP HEADER
  return (
    <React.Fragment>
      {/* MENU BUTTON */}
      <IconButton
        style={{
          paddingRight: props.view !== viewIds.mainApp ? "10px" : "unset",
        }}
        onClick={handleClick}
        size="small"
      >
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor={"right"}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        className={classes.drawerRoot}
      >
        <List>
          {/* USERNAME */}
          <ListItem
            disabled={true}
            style={{ opacity: "1" }}
            className={classes.accountMenuItem}
          >
            <ListItemIcon>
              <AccountCircleIcon />
            </ListItemIcon>
            {props.username}
          </ListItem>

          {/* REMEMBER ME */}
          {props.rememberMeAllowed && (
            <ListItem ContainerComponent="div" className={classes.noIconItem}>
              <ListItemText primary="Remember Me" />
              <ListItemSecondaryAction>
                <Switch
                  onChange={() => handleChangeRememberMe()}
                  checked={props.rememberMe}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
          )}

          <Divider className={classes.divider} />

          {/* START PAGE MENU ITEMS*/}
          {(props.view === viewIds.newStudy ||
            props.view === viewIds.loadStudy ||
            props.view === viewIds.loadParticipantReview ||
            props.view === viewIds.customProtocol ||
            props.view === viewIds.startPage) && (
            <React.Fragment>
              <ListSubheader className={classes.listSubheader}>
                Save
              </ListSubheader>
              <ListItem
                button
                disabled
                className={classes.listItem}
                onClick={() => {
                  props.fetchLocalStudiesList();
                  props.fetchGetAvailableDrives();
                  props.toggleExportStudiesDialog();
                }}
              >
                <ListItemIcon>
                  <UsbIcon />
                </ListItemIcon>
                <ListItemText primary="Export Studies to USB" />
              </ListItem>
              {wifiEnabled && (
                <ListItem
                  button
                  disabled
                  className={classes.listItem}
                  onClick={() => {
                    props.fetchLocalStudiesList();
                    props.fetchRemoteStudiesList();
                    props.toggleSyncStudiesDialog();
                    handleCloseMenu();
                  }}
                >
                  <ListItemIcon>
                    <CloudIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sync Studies to Cloud" />
                </ListItem>
              )}

              <Divider className={classes.divider} />
              <ListItem
                button
                disabled
                className={classes.listItem}
                onClick={() => props.discardStudy("LOGOUT")}
              >
                <ListItemIcon>
                  <QuitIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItem>
              <ListItem
                button
                className={classes.listItem}
                // onClick={() => {
                //   if (
                //     window.require("electron").remote.process.platform ===
                //     "win32"
                //   ) {
                //     window
                //       .require("electron")
                //       .remote.getCurrentWindow()
                //       .close();
                //   } else if (
                //     window.require("electron").remote.process.platform ===
                //     "linux"
                //   ) {
                //     const { exec } = window.require("child_process");

                //     //exec("sudo systemctl stop my_start_server.service", (error,data,getter)=>{
                //     //   console.log(data, getter);
                //     //    });
                //     //exec("sudo systemctl stop my_start_file.service", (error,data,getter)=>{
                //     //    console.log(data, getter);
                //     //   });
                //     exec("sudo shutdown -h now", (error, data, getter) => {
                //       console.log(data, getter);
                //     });
                //   }
                // }}
              >
                <ListItemIcon>
                  <PowerSettingsNewIcon />
                </ListItemIcon>

                <ListItemText
                  primary={
                    // window.require("electron").remote.process.platform ===
                    // "linux"
                    //   ? "Shut Down"
                    //   :
                    "Exit App"
                  }
                />
              </ListItem>
            </React.Fragment>
          )}

          {/* REVIEW AND PROTOCL CREATION MENU ITEMS */}
          {((props.createProtocolMode && props.view === viewIds.mainApp) ||
            props.view === viewIds.reviewMode) && (
            <React.Fragment>
              {props.view === viewIds.reviewMode && (
                <React.Fragment>
                  <ListSubheader className={classes.listSubheader}>
                    Save
                  </ListSubheader>
                  <ListItem
                    button
                    disabled
                    className={classes.listItem}
                    onClick={() => {
                      props.toggleExportCsvDialog();
                      handleCloseMenu();
                    }}
                  >
                    <ListItemIcon>
                      <CsvIcon />
                    </ListItemIcon>
                    <ListItemText primary=" Export Studies as CSV" />
                  </ListItem>
                  <ListItem
                    button
                    disabled
                    className={classes.listItem}
                    onClick={() => {
                      props.fetchLocalStudiesList();
                      props.fetchGetAvailableDrives();
                      props.toggleExportStudiesDialog();
                      handleCloseMenu();
                    }}
                  >
                    <ListItemIcon>
                      <UsbIcon />
                    </ListItemIcon>
                    <ListItemText primary="Export Studies to USB" />
                  </ListItem>

                  <Divider className={classes.divider} />
                </React.Fragment>
              )}

              {props.createProtocolMode && props.view === viewIds.mainApp && (
                <ListItem
                  button
                  className={classes.noIconItem}
                  onClick={() => {
                    props.setCreateProtocolMode(false);
                    props.discardStudy("QUIT");
                  }}
                >
                  <ListItemText primary={"Quit Create Protocol"} />
                </ListItem>
              )}

              <ListItem
                button
                disabled
                className={classes.listItem}
                onClick={() => props.discardStudy("LOGOUT")}
              >
                <ListItemIcon>
                  <QuitIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItem>
            </React.Fragment>
          )}

          {/* STUDY INFROMATION */}
          {props.view === viewIds.mainApp && !props.createProtocolMode && (
            <React.Fragment>
              <ListSubheader
                className={classes.listSubheader}
                disableSticky={true}
                component="div"
              >
                Study
              </ListSubheader>

              <ListItem
                button
                className={classes.listItem}
                onClick={() => {
                  props.setMainViewSection("studyInfoFocused");
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <InfoIcon />
                </ListItemIcon>
                <ListItemText primary="View Study Info " />
              </ListItem>

              <ListItem ContainerComponent="div" className={classes.listItem}>
                <ListItemIcon>
                  <LockIcon />
                </ListItemIcon>

                <ListItemText primary="Lock Study" />
                <ListItemSecondaryAction>
                  <Switch
                    onChange={() => props.toggleLockDialog()}
                    checked={props.locked}
                    disabled={
                      props.acquiring ||
                      !props.samplesDone ||
                      !props.samplesSaved
                    }
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider className={classes.divider} />
              <ListSubheader className={classes.listSubheader}>
                Save
              </ListSubheader>
              <ListItem
                button
                // disabled
                className={classes.listItem}
                onClick={() => handleSaveClick("CONTINUE")}
              >
                <ListItemIcon>
                  <SaveIcon />
                </ListItemIcon>
                <ListItemText primary="Save" />
              </ListItem>
              <ListItem
                button
                // disabled
                className={classes.listItem}
                onClick={() => handleSaveClick("SAVEAS")}
              >
                <ListItemIcon>
                  <SaveIcon />
                </ListItemIcon>
                <ListItemText primary="Save As" />
              </ListItem>

              <ListItem
                button
                onClick={() =>
                  setOpenShowAllSaveOptions(!openShowAllSaveOptions)
                }
                className={classes.noIconItem}
              >
                <ListItemText primary="See All Save Options" />
                {openShowAllSaveOptions ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse
                in={openShowAllSaveOptions}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <ListItem
                    button
                    disabled
                    className={classes.listItem}
                    onClick={() => {
                      props.toggleExportPdfDialog();
                      handleCloseMenu();
                    }}
                  >
                    <ListItemIcon>
                      <PdfIcon />
                    </ListItemIcon>
                    <ListItemText primary=" Export Study as PDF" />
                  </ListItem>

                  <ListItem
                    button
                    disabled
                    className={classes.listItem}
                    onClick={() => {
                      props.fetchLocalStudiesList();
                      props.fetchGetAvailableDrives();
                      props.toggleExportStudiesDialog();
                    }}
                  >
                    <ListItemIcon>
                      <UsbIcon />
                    </ListItemIcon>
                    <ListItemText primary="Export Studies to USB" />
                  </ListItem>
                  {wifiEnabled && (
                    <ListItem
                      button
                      disabled
                      className={classes.listItem}
                      onClick={() => {
                        props.fetchLocalStudiesList();
                        props.fetchRemoteStudiesList();
                        props.toggleSyncStudiesDialog();
                        handleCloseMenu();
                      }}
                    >
                      <ListItemIcon>
                        <CloudIcon />
                      </ListItemIcon>
                      <ListItemText primary="Sync Studies to Cloud" />
                    </ListItem>
                  )}

                  <ListItem
                    button
                    className={classes.listItem}
                    disabled={
                      props.acquiring ||
                      props.mainViewSection.columnThree ===
                        mainAppSectionIds.addNewLocationSection
                    }
                    onClick={() => {
                      handleProtocolClick();
                      handleCloseMenu();
                    }}
                  >
                    <ListItemIcon>
                      <ListAltIcon />
                    </ListItemIcon>
                    <ListItemText primary="Save Custom Protocol" />
                  </ListItem>
                </List>
              </Collapse>

              {/* DEVICE INFORMATION */}
              {props.connectionEstablished && (
                <Divider className={classes.divider} />
              )}
              {props.connectionEstablished && (
                <ListSubheader className={classes.listSubheader}>
                  {`Device SN: ${props.serialNumber}`}
                </ListSubheader>
              )}
              {props.connectionEstablished && (
                <ListItem
                  button
                  className={classes.listItem}
                  disabled={props.acquiring || props.simulate}
                  onClick={() => handleHealthClick()}
                >
                  <ListItemIcon>
                    <AssessmentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Run Device Diagnostics" />
                </ListItem>
              )}

              <Divider className={classes.divider} />

              <ListItem
                button
                className={classes.noIconItem}
                onClick={() => handleSaveClick("QUIT")}
              >
                <ListItemText primary="Quit Study" />
              </ListItem>
              <ListItem
                button
                disabled
                className={classes.listItem}
                onClick={() => handleSaveClick("LOGOUT")}
              >
                <ListItemIcon>
                  <QuitIcon />
                </ListItemIcon>
                <ListItemText primary="Quit Study and Logout" />
              </ListItem>
            </React.Fragment>
          )}

          <Divider className={classes.divider} />

          <ListItem
            button
            onClick={() => setOpenAdvanceSettings(!openAdvanceSettings)}
            className={classes.noIconItem}
          >
            <ListItemText primary="Advanced Settings" />
            {openAdvanceSettings ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse
            className={classes.advancedSettingsCollapse}
            in={openAdvanceSettings}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              {/* VIRTUAL KEYBOARD */}
              <ListItem ContainerComponent="div" className={classes.listItem}>
                <ListItemIcon>
                  <KeyboardIcon />
                </ListItemIcon>

                <ListItemText primary="Virtual Keyboard" />
                <ListItemSecondaryAction>
                  <Switch
                    onChange={() => props.toggleVirtualKeyboardAllowed()}
                    checked={props.virtualKeyboardAllowed}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem ContainerComponent="div" className={classes.noIconItem}>
                <ListItemText primary="Dark Theme" />
                <ListItemSecondaryAction>
                  <Switch
                    onChange={() => {
                      document.body.style.setProperty(
                        "background-color",
                        props.theme === "dark"
                          ? colors.light.background
                          : colors.dark.background
                      );
                      props.setTheme();
                    }}
                    checked={props.theme === "dark"}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <ListItem
                button
                className={classes.listItem}
                onClick={() => {
                  props.fetchCheckWifi(true, false);
                  props.toggleWifiDialog();
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <WifiIcon />
                </ListItemIcon>
                <ListItemText primary="Wifi Connection" />
              </ListItem>

              <ListItem
                button
                disabled
                className={classes.listItem}
                onClick={() => {
                  props.fetchCheckUpdate();
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <UpdateIcon />
                </ListItemIcon>
                <ListItemText primary="Check for Software Update" />
              </ListItem>

              {/* {props.view === viewIds.mainApp && !props.createProtocolMode && (
                <React.Fragment>
                  <ListItem
                    ContainerComponent="div"
                    className={classes.listItem}
                  >
                    <ListItemIcon>
                      <DeviceIcon />
                    </ListItemIcon>
                    <ListItemText primary="Device Simulation" />
                    <ListItemSecondaryAction>
                      <Switch
                        onChange={(e) => handleToggleSimulate(e)}
                        checked={props.simulate}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              )} */}

              {/* {window
                .require("electron")
                .remote.getCurrentWindow()
                .isFullScreen() && (
                <ListItem
                  button
                  className={classes.listItem}
                  onClick={() => {
                    window
                      .require("electron")
                      .remote.getCurrentWindow()
                      .setFullScreen(false);
                  }}
                >
                  <ListItemIcon>
                    <FullscreenExitIcon />
                  </ListItemIcon>
                  <ListItemText primary="Exit Fullscreen" />
                </ListItem>
              )} */}
            </List>
          </Collapse>
        </List>

        <List>
          <ListItem
            disabled={true}
            ContainerComponent="div"
            className={classes.versionListItem}
          >
            <ListItemText primary={`Application Version: ${props.version}`} />
          </ListItem>

          <ListItem
            disabled={true}
            ContainerComponent="div"
            className={classes.versionListItem}
          >
            <ListItemText
              primary={`Server Version: ${
                props.serverVersion === null ? "-" : props.serverVersion
              }`}
            />
          </ListItem>
        </List>
      </Drawer>

      {/* Only mount the dialogs if they are open */}
      {props.studySaveDialog && (
        <SaveStudyDialog
          open={props.studySaveDialog}
          // eventSource={props.eventSource}
          initialPath={initialPath}
          initialName={initialName}
          extension={extension}
          prompt={saveStudyPrompt.prompt}
          resetPrompts={() => {
            setSaveStudyPrompt({ prompt: null });
          }}
          onClose={() => {
            props.toggleSaveDialog();
          }}
        />
      )}

      {props.wifiDialog && (
        <WifiDialog
          open={props.wifiDialog}
          onClose={() => props.toggleWifiDialog()}
        />
      )}

      {props.saveProtocolDialog && (
        <SaveProtocolDialog
          open={props.saveProtocolDialog}
          onClose={() => {
            props.toggleProtocolDialog();
            handleCloseMenu();
          }}
        />
      )}
      {props.exportPdfDialog && (
        <ExportPdfDialog
          open={props.exportPdfDialog}
          initialPath={initialPath}
          initialName={initialName}
          // eventSource={props.eventSource}
          onClose={() => {
            props.toggleExportPdfDialog();
          }}
        />
      )}
      {props.exportCsvDialog && (
        <ExportCsvDialog
          open={props.exportCsvDialog}
          // eventSource={props.eventSource}
          onClose={() => {
            props.toggleExportCsvDialog();
          }}
        />
      )}
      {props.exportStudiesDialog && (
        <ExportStudiesDialog
          open={props.exportStudiesDialog}
          // eventSource={props.eventSource}
          onClose={() => {
            props.toggleExportStudiesDialog();
          }}
        />
      )}
      {props.syncStudiesDialog && (
        <SyncStudiesDialog
          open={props.syncStudiesDialog}
          // eventSource={props.eventSource}
          onClose={() => {
            props.toggleSyncStudiesDialog();
          }}
        />
      )}
      {props.quitDialog && (
        <QuitDialog
          open={props.quitDialog}
          handleForceQuit={() => {
            handleCloseMenu();
            handleForceQuit();
          }}
          onClose={() => {
            props.toggleQuitDialog(); // close dialog
            handleCloseMenu(); // close menu
          }}
          theme={props.theme}
        />
      )}
      {props.studyLockDialog && (
        <LockStudyDialog
          open={props.studyLockDialog}
          onClose={() => {
            props.toggleLockDialog(); // close dialog
            handleCloseMenu(); // close menu
          }}
        />
      )}
    </React.Fragment>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      errorResponse,
      fetchSaveStudy,
      toggleLock,
      toggleLockDialog,
      toggleSaveDialog,
      toggleProtocolDialog,
      fetchCheckSavedStudy,
      setMainViewSection,
      toggleDiagnosticsDialog,
      toggleExportPdfDialog,
      toggleExportCsvDialog,
      toggleExportStudiesDialog,
      toggleVirtualKeyboardAllowed,
      setTheme,
      toggleQuitDialog,
      setCreateProtocolMode,
      discardStudy,
      fetchCheckWifi,
      fetchGetAvailableDrives,
      fetchLocalStudiesList,
      toggleSkipDiagnosticsSetup,
      fetchCloseDevice,
      toggleWifiDialog,
      fetchCheckUpdate,
      toggleSyncStudiesDialog,
      fetchRemoteStudiesList,
      fetchConnectDevice,
      setRememberMe,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    view: state.view.viewId,
    locked: state.studyInfo.locked,
    studyLockDialog: state.utility.dialog.studyLockDialog, // hide/show lock study dialog
    studySaveDialog: state.utility.dialog.studySaveDialog,
    saveProtocolDialog: state.utility.dialog.saveProtocolDialog,
    exportPdfDialog: state.utility.dialog.exportPdfDialog,
    exportCsvDialog: state.utility.dialog.exportCsvDialog,
    exportStudiesDialog: state.utility.dialog.exportStudiesDialog,
    syncStudiesDialog: state.utility.dialog.syncStudiesDialog,
    wifiDialog: state.utility.dialog.wifiDialog,
    quitDialog: state.utility.dialog.quitDialog,
    studySaved: state.studyInfo.studySaved,
    studySavePath: state.studyInfo.studySavePath,
    studyId: state.studyInfo.id,
    encryption: state.studyInfo.encryption,
    samplesDone: state.utility.samplesDone,
    samplesSaved: state.utility.samplesSaved,
    connectionEstablished: state.device.connectionEstablished,
    acquiring: state.device.acquiring,
    username: state.userInfo.username,
    password: state.userInfo.password,
    mainViewSection: state.view.mainViewSection,
    rememberMeAllowed: state.userInfo.rememberMeAllowed,
    rememberMe: state.userInfo.rememberMe,
    virtualKeyboardAllowed: state.utility.virtualKeyboardAllowed,
    createProtocolMode: state.utility.mode.createProtocolMode,
    simulate: state.device.simulate,
    serialNumber:
      state.device.connectionEstablished && !state.device.simulate
        ? state.device.serialNumber
        : "",

    version: state.studyInfo.version,
    serverVersion: state.studyInfo.serverVersion,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(HeaderMenu));
